import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';

const QuoteSimple = () => {
  const [searchParams] = useSearchParams();
  const { products, loading: productsLoading } = useProducts();
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    company: '',
    product_id: searchParams.get('product') || 'cloud20',
    quantity: 1,
    additional_options: [],
    message: ''
  });

  const additionalOptions = [
    { value: 'installation', label: 'Installation complète' },
    { value: 'maintenance', label: 'Contrat maintenance annuel' },
    { value: 'autonomy', label: 'Kit autonomie solaire' },
    { value: 'extended_warranty', label: 'Garantie étendue 5 ans' },
    { value: 'monitoring', label: 'Système monitoring IoT' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (optionValue) => {
    setFormData(prev => ({
      ...prev,
      additional_options: prev.additional_options.includes(optionValue)
        ? prev.additional_options.filter(opt => opt !== optionValue)
        : [...prev.additional_options, optionValue]
    }));
  };

  const generateEmailContent = () => {
    const selectedProduct = products.find(p => p.id === formData.product_id);
    const selectedOptionsText = formData.additional_options
      .map(optionValue => {
        const option = additionalOptions.find(opt => opt.value === optionValue);
        return option ? option.label : '';
      })
      .filter(Boolean)
      .join(', ');

    return `Bonjour,

Je souhaite recevoir un devis pour :

INFORMATIONS CLIENT:
- Nom: ${formData.first_name} ${formData.last_name}
- Email: ${formData.email}
- Téléphone: ${formData.phone}
${formData.company ? `- Entreprise: ${formData.company}` : ''}

PRODUIT DEMANDÉ:
- Modèle: ${selectedProduct ? selectedProduct.name : ''}
- Capacité: ${selectedProduct ? selectedProduct.capacity_liters + 'L/jour' : ''}
- Quantité: ${formData.quantity}

${selectedOptionsText ? `OPTIONS: ${selectedOptionsText}` : ''}

${formData.message ? `MESSAGE: ${formData.message}` : ''}

Cordialement,
${formData.first_name} ${formData.last_name}`;
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat text-white py-20"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/40784/drops-of-water-water-nature-liquid-40784.jpeg?auto=compress&cs=tinysrgb&w=1920')"
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 drop-shadow-lg">
            Demande de devis gratuit
          </h1>
          <p className="text-xl lg:text-2xl text-blue-100 drop-shadow-md">
            Remplissez ce formulaire et nous vous contacterons rapidement
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-8">
              {/* Informations personnelles */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vos informations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Votre nom"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0590 XX XX XX"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nom de votre entreprise (optionnel)"
                  />
                </div>
              </div>

              {/* Sélection produit */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Produit d'intérêt</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Produit *</label>
                    <select
                      name="product_id"
                      value={formData.product_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {product.capacity_liters}L/jour
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quantité *</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Options supplémentaires */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Options supplémentaires</h3>
                <div className="space-y-3">
                  {additionalOptions.map((option) => (
                    <label key={option.value} className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.additional_options.includes(option.value)}
                        onChange={() => handleCheckboxChange(option.value)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{option.label}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Décrivez votre projet, vos besoins spécifiques..."
                />
              </div>

              {/* Boutons d'action */}
              <div className="space-y-4">
                <a
                  href={`mailto:contact@ecoclima.energy?subject=Demande de devis ECOCLIMA&body=${encodeURIComponent(generateEmailContent())}`}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors text-center block"
                >
                  Envoyer ma demande par email
                </a>
                
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">Ou appelez-nous directement</p>
                  <a
                    href="tel:+590590197070"
                    className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    0590 19 70 70
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé de votre demande</h3>
              
              {selectedProduct && (
                <>
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900">{selectedProduct.name}</h4>
                    <p className="text-sm text-gray-600">{selectedProduct.capacity_liters}L/jour</p>
                    <p className="text-sm text-gray-600">Quantité: {formData.quantity}</p>
                  </div>

                  {formData.additional_options.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-2">Options sélectionnées:</h4>
                      {formData.additional_options.map(optionValue => {
                        const option = additionalOptions.find(opt => opt.value === optionValue);
                        return option ? (
                          <div key={optionValue} className="text-sm text-gray-600">
                            • {option.label}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="text-center">
                      <span className="text-lg font-semibold text-green-600">Prix sur devis</span>
                      <p className="text-sm text-gray-500 mt-2">
                        Nous vous contacterons rapidement avec une offre personnalisée.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteSimple;